import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle
} from '@mui/material'
import { AlertRuleDataGridRow } from './AlertRuleDataGrid'
import { Device, Project, RuuviTag, Valve } from '../types'
import { AlertGroup, AlertRule, AlertTemplate } from '../types/Alerts'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { LoadingButton } from '@mui/lab'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { useState } from 'react'
import {
  buildAlertStructure,
  isDeviceAlertGroup,
  isProjectAlertGroup,
  isRuuviTagAlertGroup,
  isValveAlertGroup,
  makeAlertRuleDataGridRow
} from '../utils/alert'
import { getProjectAlertRules, updateProjectAlertRules } from '../services/projectService'
import { getRuuviTagAlertRules, updateRuuviTagAlertRules } from '../services/ruuviTagService'
import { getDeviceAlertRules, updateDeviceAlertRules } from '../services/deviceService'
import { setProjectAlertRules } from '../redux/slices/projects'
import { setAlertRules as setRuuviTagAlertRules } from '../redux/slices/ruuviTag'
import { setAlertRules as setDeviceAlertRules } from '../redux/slices/devices'
import { setAlertRules as setValveAlertRules } from '../redux/slices/valve'
import { isSpine, isValve } from '../utils/device'
import { getValveAlertRules, updateValveAlertRules } from '../services/valve'
import { useAlertRules } from '../hooks'

export interface AlertRuleRemoveDialogProps extends Omit<DialogProps, 'children' | 'onClose'> {
  alertRules: AlertGroup[]
  alertTemplates: AlertTemplate[]
  rule: string
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  useRedux?: boolean
  onClose?: () => void
  beforeSubmit?: (input: AlertRuleDataGridRow[]) => void
  afterSubmit?: (alertRules: AlertGroup[]) => void
}

export default function AlertRuleRemoveDialog({
  project,
  ruuviTag,
  device,
  alertRules,
  alertTemplates,
  rule,
  open,
  maxWidth = 'sm',
  fullWidth = true,
  useRedux = true,
  onClose,
  beforeSubmit,
  afterSubmit,
  ...props
}: AlertRuleRemoveDialogProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * Indicates if the alert rule is being deleted.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * The alerts rows.
   */
  const { enabledAlertRules, updateAlertRules } = useAlertRules({
    project,
    device,
    ruuviTag,
    alertTemplates,
    alertRules,
    useRedux
  })

  /**
   * Handle the submit.
   */
  const handleSubmit = async () => {
    try {
      const input = enabledAlertRules.map((row) =>
        row.id === rule ? { ...row, enabled: false } : row
      )
      setSubmitting(true)
      beforeSubmit?.(input)

      const updatedAlertRules = await updateAlertRules(input)
      setSubmitting(false)
      dispatch(createSuccessNotification(t('disableAlertRuleSuccess')))
      afterSubmit?.(updatedAlertRules)
    } catch (_error) {
      setSubmitting(false)
      dispatch(createErrorNotification(t('disableAlertRuleError')))
    }
  }

  return (
    <Dialog open={open} maxWidth={maxWidth} fullWidth={fullWidth} {...props} onClose={onClose}>
      <DialogTitle>{t('deleteAlertRule')}</DialogTitle>
      <DialogContent>
        <Alert severity="error">{t('deleteAlertRuleConfirmation')}</Alert>
      </DialogContent>
      <DialogActions sx={{ pt: 0 }}>
        <Button variant="text" color="inherit" disabled={isSubmitting} onClick={onClose}>
          {t('cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          {t('confirm')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
